import DefaultLink from '@/components/common/default/DefaultLink';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';
import DefaultButton from '../../common/default/DefaultButton';
import { HeaderEnum } from '@/types/translations/HeaderEnum';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import useBackendUser from '@/hooks/useBackendUser';
import {
  User,
  LayoutDashboard,
  ShoppingCart,
  Tag,
  Bell,
  Briefcase,
  Heart,
  Eye,
  LogOut,
  Send,
} from 'lucide-react';
import getUserUsername from '@/utilites/get-user-username';
import { Routes } from '@/types/routes.enum';
import HeaderUserDropdownLanguage from './HeaderUserDropdownLanguage';
import HeaderUserDropdownSettings from './HeaderUserDropdownSettings';
import { useCtaCount } from '@/useQuery/queries/notifications';
import HeaderCtaCount from './HeaderCtaCount';
export default function HeaderUserDropdown() {
  const ctaCountQuery = useCtaCount();
  const { data: user, isLoading: isUserLoading } = useBackendUser();
  const { translate } = useTypedTranslation<HeaderEnum>('header');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        event.button !== 2 &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => setIsOpen(prev => !prev);

  const baseItemClass =
    'tw-whitespace-nowrap tw-w-full tw-h-full tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-charcoal-blue tw-pr-24 tw-pl-2 tw-py-2 hover:tw-bg-gray-100 tw-rounded-md tw-cursor-pointer tw-transition-colors tw-duration-200 tw-font-normal tw-pl-4 tw-pr-24';

  const iconClass = 'tw-stroke-charcoal-blue tw-w-5 tw-h-5';

  return (
    <nav ref={dropdownRef} className="tw-relative tw-inline-block">
      <DefaultButton
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggleDropdown}
        className="tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-offset-2 tw-focus:ring-main/30 tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-xl tw-border tw-border-solid tw-border-gray-300 tw-p-2 tw-px-4 tw-text-lg tw-font-semibold tw-leading-6 tw-text-textBlack tw-shadow-lg hover:tw-text-main"
      >
        <User className="tw-text-2xl" />
        {getUserUsername(user, isUserLoading).split(' ')[0]}
        {Number(ctaCountQuery.data?.totalNotificationsCallToAc) > 0 && (
          <div className="tw-absolute tw-right-0 tw-top-0">
            <span className="tw-flex tw-h-6 tw-w-6 tw-translate-x-1/3 tw-translate-y-[-25%] tw-transform tw-items-center tw-justify-center tw-rounded-full tw-bg-red-500 tw-text-xs tw-font-bold tw-text-white tw-shadow-sm">
              {Number(ctaCountQuery.data?.totalNotificationsCallToAc) > 99
                ? '99+'
                : Number(ctaCountQuery.data?.totalNotificationsCallToAc) > 9
                  ? '9+'
                  : Number(ctaCountQuery.data?.totalNotificationsCallToAc)}
            </span>
          </div>
        )}
      </DefaultButton>

      <Transition
        show={isOpen}
        enter="tw-transition tw-ease-out tw-duration-200"
        enterFrom="tw-opacity-0 tw-scale-95"
        enterTo="tw-opacity-100 tw-scale-100"
        leave="tw-transition tw-ease-in tw-duration-150"
        leaveFrom="tw-opacity-100 tw-scale-100"
        leaveTo="tw-opacity-0 tw-scale-95"
        as={Fragment}
      >
        <div className="tw-absolute tw-right-0 tw-z-[999] tw-mt-2 tw-min-w-40 tw-rounded-xl tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-shadow-xl">
          <ul className="w-fit tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0 tw-py-2 tw-pb-0">
            <li className="tw-flex tw-items-center tw-justify-start">
              <DefaultLink href={Routes._DASHBOARD} className={`${baseItemClass} tw-pr-4`}>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <LayoutDashboard className={iconClass} />
                  {translate(HeaderEnum['dashboard_menu.main.dashboard'])}
                </div>
              </DefaultLink>
            </li>
            <li className="tw-w-full">
              <DefaultLink href={Routes._DASHBOARD_BUYING} className={`${baseItemClass} tw-pr-4`}>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <ShoppingCart className={iconClass} />
                  {translate(HeaderEnum['dashboard_menu.main.buying'])}
                </div>
                <HeaderCtaCount cta={ctaCountQuery.data?.buyerNotificationsCallToAc ?? 0} />
              </DefaultLink>
            </li>
            <li className="tw-w-full">
              <DefaultLink href={Routes._DASHBOARD_SELLING} className={`${baseItemClass} tw-pr-4`}>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <Tag className={iconClass} />
                  {translate(HeaderEnum['dashboard_menu.main.selling'])}
                </div>
                <HeaderCtaCount cta={ctaCountQuery.data?.sellerNotificationsCallToAc ?? 0} />
              </DefaultLink>
            </li>
            <li>
              <DefaultLink href={Routes._DASHBOARD_NOTIFICATIONS} className={`${baseItemClass}`}>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <Bell className={iconClass} />
                  {translate(HeaderEnum['dashboard_menu.main.notifications'])}
                </div>
              </DefaultLink>
            </li>
            <li>
              <DefaultLink href={Routes._DASHBOARD_PORTFOLIO} className={`${baseItemClass}`}>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <Briefcase className={iconClass} />
                  {translate(HeaderEnum['dashboard_menu.main.portfolio'])}
                </div>
              </DefaultLink>
            </li>
            <li>
              <DefaultLink href={Routes._DASHBOARD_WISHLIST} className={`${baseItemClass}`}>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <Heart className={iconClass} />
                  {translate(HeaderEnum['dashboard_menu.main.wishlist'])}
                </div>
              </DefaultLink>
            </li>
            <li>
              <DefaultLink href={Routes._DASHBOARD_LAST_VIEWED} className={`${baseItemClass}`}>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <Eye className={iconClass} />
                  {translate(HeaderEnum['dashboard_menu.main.last_viewed'])}
                </div>
              </DefaultLink>
            </li>
            <li className="tw-border tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-gray-300 tw-py-4">
              <DefaultLink href={Routes._DASHBOARD_SUGGEST_BOTTLE} className={`${baseItemClass}`}>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <Send className={iconClass} />
                  {translate(HeaderEnum['dashboard_menu.main.suggest_a_bottle'])}
                </div>
              </DefaultLink>
            </li>
            <HeaderUserDropdownSettings
              settingsDropdownOpen={settingsDropdownOpen}
              setSettingsDropdownOpen={value => {
                setSettingsDropdownOpen(value);
                setLanguageDropdownOpen(false);
              }}
            />
            <HeaderUserDropdownLanguage
              languageDropdownOpen={languageDropdownOpen}
              setLanguageDropdownOpen={value => {
                setLanguageDropdownOpen(value);
                setSettingsDropdownOpen(false);
              }}
            />
            <li className="tw-flex tw-items-center tw-justify-start tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-t tw-border-solid tw-border-gray-300 tw-pt-4">
              <a href={Routes._AUTH_LOGOUT} className={`${baseItemClass} tw-no-underline`}>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <LogOut className={iconClass} />
                  {translate(HeaderEnum['mobile_menu.sign_out'])}
                </div>
              </a>
            </li>
          </ul>
        </div>
      </Transition>
    </nav>
  );
}
